import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import FragranceCard, { Fragrance } from './FragranceCard';
import LoadingSpinner from '../ui/LoadingSpinner';

interface FragranceGridProps {
  fragrances: Fragrance[];
  isLoading?: boolean;
  onEdit?: (fragrance: Fragrance) => void;
  onDelete?: (fragranceId: number) => void;
  onToggleFavorite?: (fragranceId: number) => void;
  onCopyDetails?: (fragrance: Fragrance) => void;
}

const FragranceGrid: React.FC<FragranceGridProps> = ({
  fragrances,
  isLoading = false,
  onEdit,
  onDelete,
  onToggleFavorite,
  onCopyDetails
}) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <LoadingSpinner size="lg" className="mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading your collection...</p>
        </div>
      </div>
    );
  }

  if (fragrances.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="text-6xl mb-4">🌸</div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          No fragrances found
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto">
          Start building your collection by adding your first fragrance, or adjust your search filters to find existing ones.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-8">
      <AnimatePresence mode="popLayout">
        {fragrances.map((fragrance, index) => (
          <motion.div
            key={fragrance.id}
            layout
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: -20 }}
            transition={{
              duration: 0.3,
              delay: index * 0.05, // Stagger animation
              layout: { duration: 0.3 }
            }}
          >
            <FragranceCard
              fragrance={fragrance}
              onEdit={onEdit}
              onDelete={onDelete}
              onToggleFavorite={onToggleFavorite}
              onCopyDetails={onCopyDetails}
            />
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};

export default FragranceGrid;
